'use client';

import { jsxs, jsx, Fragment } from "react/jsx-runtime";
import { RequiredIndicator } from "../../quarks/RequiredIndicator/RequiredIndicator.js";
import { classNames } from "../../utils/styles/classNames.js";
import { Text } from "../Text/Text.js";
import { WarningOctagon } from "../../../react-icons.js";
import { getMaxCharactersA11yMessage } from "../Textarea/utils/getMaxCharactersA11yMessage.js";
import { errorWrapper, label, gap, container, counter, headerWrapperMargin, headerAlignment, headerWrapper } from "./FormControl.css.js";
import { errorLabel } from "./FormControl.data.js";
function FormControl({
  caption,
  descriptionPosition,
  descriptionId,
  children,
  disabled = false,
  error,
  hiddenLabel = false,
  label: label$1,
  a11yLabel,
  required = false,
  a11yAnnounceRequired = false,
  className,
  charactersCounter
}) {
  const leadDescription = descriptionPosition === "lead";
  const hasCounter = !!(charactersCounter == null ? void 0 : charactersCounter.max);
  const hasHeader = !hiddenLabel || hasCounter;
  const hiddenCaption = hiddenLabel && leadDescription;
  const captionJsx = caption && /* @__PURE__ */ jsx(
    Text,
    {
      as: "p",
      color: disabled ? "onLight.disabled.muted" : "onLight.text.secondary",
      id: descriptionId,
      variant: "help-text.xs",
      visuallyHidden: hiddenCaption,
      children: caption
    }
  );
  const errorJsx = error && /* @__PURE__ */ jsxs("div", { className: errorWrapper, children: [
    error.length && /* @__PURE__ */ jsx(WarningOctagon, { color: "action.destructive.darkest", size: "sm" }),
    error.length && /* @__PURE__ */ jsx(Text, { visuallyHidden: true, children: errorLabel }),
    /* @__PURE__ */ jsx(
      Text,
      {
        as: "p",
        color: "action.destructive.darkest",
        id: descriptionId,
        variant: "help-text.xs",
        children: error
      }
    )
  ] });
  const requiredJsx = required && /* @__PURE__ */ jsx(
    RequiredIndicator,
    {
      a11yAnnounce: a11yAnnounceRequired,
      disabled
    }
  );
  const labelTextVariant = "label.md";
  const labelJsx = /* @__PURE__ */ jsxs(
    Text,
    {
      ...label$1,
      as: "label",
      className: classNames(label),
      color: disabled ? "onLight.disabled.muted" : "onLight.text.primary",
      variant: labelTextVariant,
      visuallyHidden: hiddenLabel,
      children: [
        /* @__PURE__ */ jsx(
          Text,
          {
            "aria-hidden": a11yLabel ? "true" : void 0,
            variant: labelTextVariant,
            children: label$1.children
          }
        ),
        a11yLabel && /* @__PURE__ */ jsx(Text, { visuallyHidden: true, children: a11yLabel }),
        requiredJsx
      ]
    }
  );
  const contentContainerClassName = classNames(
    container,
    gap[leadDescription ? "withLeadDescription" : "default"]
  );
  const descriptionJsx = errorJsx || captionJsx;
  const headerJsx = (charactersCounter == null ? void 0 : charactersCounter.max) ? /* @__PURE__ */ jsxs(Fragment, { children: [
    labelJsx,
    /* @__PURE__ */ jsxs("span", { children: [
      /* @__PURE__ */ jsxs(
        Text,
        {
          className: counter,
          color: disabled ? "onLight.disabled.muted" : "onLight.text.tertiary",
          variant: "help-text.xs",
          children: [
            charactersCounter.current,
            "/",
            charactersCounter.max
          ]
        }
      ),
      /* @__PURE__ */ jsx(Text, { "aria-live": "polite", visuallyHidden: true, children: getMaxCharactersA11yMessage(
        charactersCounter.max,
        charactersCounter.current
      ) })
    ] })
  ] }) : labelJsx;
  const headerClassNames = hasHeader ? classNames(
    headerWrapper,
    headerAlignment[hasCounter && hiddenLabel ? "counterOnly" : "default"],
    headerWrapperMargin[leadDescription ? "withLeadDescription" : "default"]
  ) : void 0;
  return /* @__PURE__ */ jsxs("div", { className: classNames(container, className), children: [
    /* @__PURE__ */ jsx("div", { className: headerClassNames, children: headerJsx }),
    /* @__PURE__ */ jsxs("div", { className: contentContainerClassName, children: [
      leadDescription && descriptionJsx,
      children,
      !leadDescription && descriptionJsx
    ] })
  ] });
}
export {
  FormControl
};
